import { IconButton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from '@mui/icons-material/Search';
import Fuse from 'fuse.js';
import { useCallback, useEffect, useState } from 'react';
function fuzzySearch(rows, query, category, units, customFieldConfigs) {
  let fuseOptionsKeys;
  if (category === 'Journalists' || category === 'Community Members') {
    fuseOptionsKeys = ['name', 'Org.name', 'unitName', 'notes', 'Organization.name'];
  } else if (category === 'Experts') {
    fuseOptionsKeys = [
      'name',
      {
        name: 'unitNames',
        getFn: (expert) => {
          if (!expert.units || expert.units.length === 0) {
            return [];
          }
          return expert.units
            .map((unitId) => {
              const u = units.find((unit) => unit._id === unitId);
              return u ? u.name : null;
            })
            .filter(Boolean);
        },
      },
      'notes',
    ];
  } else {
    fuseOptionsKeys = ['name'];
  }

  if (customFieldConfigs) {
    fuseOptionsKeys = [...fuseOptionsKeys, ...customFieldConfigs.map((c) => c.fieldKey)];
  }

  const options = {
    threshold: 0.2,
    ignoreLocation: true,
    keys: fuseOptionsKeys,
  };
  const fuse = new Fuse(rows, options);
  const results = fuse.search(query).map((result) => result.item);
  return results;
}

const useStyles = makeStyles((theme) => ({
  contactFuzzySearchTextField: { width: '600px' },
  searchIcon: { backgroundColor: '#DEDEDE', borderRadius: '4px' },
}));

const ContactFuzzySearch = (props) => {
  const classes = useStyles();
  const { contacts, setSearchResults, category, units, customFieldConfigs } = props;

  const [textFieldValue, setTextFieldValue] = useState('');

  const getFuzzySearchPlaceHolder = () => {
    let customFieldLabels = '';
    if (customFieldConfigs && customFieldConfigs.length > 0) {
      customFieldLabels = `/${customFieldConfigs.map((c) => c.label).join('/')}`;
    }

    switch (category) {
      case 'Journalists':
        return `Name/Outlet/Notes${customFieldLabels}`;
      case 'Experts':
        return `Name/Unit/Notes${customFieldLabels}`;
      case 'Community Members':
        return `Name/Organization/Notes${customFieldLabels}`;
      case 'Organizations':
        return `Name${customFieldLabels}`;
      case 'Outlets':
        return `Name${customFieldLabels}`;
      default:
        return 'Search...';
    }
  };

  const handleSearch = useCallback(
    (keywords) => {
      if (keywords !== '') {
        const results = fuzzySearch(contacts, keywords, category, units, customFieldConfigs);
        const sortedResults = [...results].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
        setSearchResults(sortedResults);
      } else {
        const sortedContacts = [...contacts].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
        setSearchResults(sortedContacts);
      }
    },
    [contacts, category, setSearchResults, units, customFieldConfigs]
  );

  const handleChange = (e) => {
    setTextFieldValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(textFieldValue);
    }
  };

  const handleSearchButtonClick = (e) => {
    handleSearch(textFieldValue);
  };

  //reset search if category changed
  useEffect(() => {
    setTextFieldValue('');
  }, [category]);

  return (
    <TextField
      variant="outlined"
      size="small"
      placeholder={getFuzzySearchPlaceHolder()}
      value={textFieldValue}
      onChange={handleChange}
      data-cy="fuzzySearch-input-ContactFuzzySearch"
      className={classes.contactFuzzySearchTextField}
      onKeyDown={handleKeyPress}
      InputProps={{
        style: { paddingRight: 0 },
        endAdornment: (
          <IconButton onClick={handleSearchButtonClick} className={classes.searchIcon}>
            <SearchIcon />
          </IconButton>
        ),
      }}
    />
  );
};

export default ContactFuzzySearch;
